import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useColorMode } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';

export type AppColorMode = 'light' | 'dark';
export type AppBackgroundTone = 'white' | 'neutral' | 'warm';

type ThemePreference = {
  colorMode: AppColorMode;
  backgroundTone: AppBackgroundTone;
};

type ThemePreferenceContextValue = ThemePreference & {
  setColorModePreference: (mode: AppColorMode) => void;
  setBackgroundTone: (tone: AppBackgroundTone) => void;
  resetPreferences: () => void;
};

const DEFAULT_PREFERENCES: ThemePreference = {
  colorMode: 'light',
  backgroundTone: 'white',
};

const BACKGROUNDS: Record<AppColorMode, Record<AppBackgroundTone, string>> = {
  light: {
    white: '#ffffff',
    neutral: '#f7f4ee',
    warm: '#f4efe8',
  },
  dark: {
    white: '#0f0f10',
    neutral: '#130d10',
    warm: '#1a1114',
  },
};

const ThemePreferenceContext = createContext<ThemePreferenceContextValue | null>(null);

const getStorageKey = (userId?: string | number | null) => `totolac-theme:${userId || 'guest'}`;

const readPreferences = (key: string): ThemePreference => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return DEFAULT_PREFERENCES;
    const parsed = JSON.parse(raw);
    return {
      colorMode: parsed.colorMode === 'dark' ? 'dark' : 'light',
      backgroundTone: parsed.backgroundTone === 'white' ? 'white' : parsed.backgroundTone === 'warm' ? 'warm' : 'neutral',
    };
  } catch {
    return DEFAULT_PREFERENCES;
  }
};

export function AppThemePreferenceProvider({ children }: { children: React.ReactNode }) {
  const user = useSelector((state: RootState) => state.auth.user);
  const { setColorMode } = useColorMode();
  const [preferences, setPreferences] = useState<ThemePreference>(DEFAULT_PREFERENCES);

  const storageKey = useMemo(() => getStorageKey(user?.id), [user?.id]);

  useEffect(() => {
    const next = readPreferences(storageKey);
    setPreferences(next);
    setColorMode(next.colorMode);
  }, [storageKey, setColorMode]);

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(preferences));
    setColorMode(preferences.colorMode);

    const bg = BACKGROUNDS[preferences.colorMode][preferences.backgroundTone];
    document.documentElement.style.setProperty('--app-bg', bg);
    document.documentElement.style.setProperty('--app-surface', preferences.colorMode === 'dark' ? '#1e1519' : '#ffffff');
    document.documentElement.style.setProperty('--app-border', preferences.colorMode === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(104,26,45,0.10)');
    document.body.style.background = bg;
  }, [preferences, setColorMode, storageKey]);

  const value = useMemo<ThemePreferenceContextValue>(() => ({
    ...preferences,
    setColorModePreference: (mode) => setPreferences((current) => ({ ...current, colorMode: mode })),
    setBackgroundTone: (tone) => setPreferences((current) => ({ ...current, backgroundTone: tone })),
    resetPreferences: () => setPreferences(DEFAULT_PREFERENCES),
  }), [preferences]);

  return (
    <ThemePreferenceContext.Provider value={value}>
      {children}
    </ThemePreferenceContext.Provider>
  );
}

export function useAppThemePreference() {
  const context = useContext(ThemePreferenceContext);
  if (!context) {
    throw new Error('useAppThemePreference must be used inside AppThemePreferenceProvider');
  }
  return context;
}
